import React, { VFC, useMemo, useState } from 'react';

import { setStatusBarBackgroundColor, setStatusBarStyle } from 'expo-status-bar';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import LegalDisclaimers from 'components/legal-disclaimers';
import NutritionModal from 'components/nutrition';
import { AllergensDisclaimer } from 'components/nutrition/allergens-disclaimer';
import { useNutritionFacts } from 'components/nutrition/use-nutrition-facts';
import { TextWithArrowButton } from 'components/text-with-arrow-button';
import { useRoute } from 'navigation/hooks';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { primitive } from 'styles/constants/primitives';
import { isItemPickerOrComboRoute } from 'utils/routing';

import { transformUserSelectionsComboToComboSlotSelections } from '../utils';

import {
  InformationTitle,
  NutritionInformationContainer,
  NutritionInformationWrapper,
} from './nutrition-information.styled';

const NutritionInformation: VFC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { pathname } = useRoute();
  const { selectedProduct, userSelections } = useProductWizardContext();

  const enableAllergensDisclaimer = useFlag(LaunchDarklyFlag.ENABLE_ALLERGENS_DISCLAIMER);

  // TODO: Remove this transform and refactor the hook to support this new data structure
  const comboSlotSelections = useMemo(
    () => transformUserSelectionsComboToComboSlotSelections(userSelections.comboSlot),
    [userSelections.comboSlot]
  );

  const nutritionFacts = useNutritionFacts({
    pickerSelection: selectedProduct,
    comboSlotSelections,
    itemName: selectedProduct?.name?.locale,
    itemImage: selectedProduct.image,
  });

  const dismiss = () => {
    // Currently we update the status bar color for android on the new PDP design.
    // Because the Modal component updates the status bar styles,
    // then we should restore the status bar color for android.
    if (isItemPickerOrComboRoute(pathname) && Platform.OS === 'android') {
      setStatusBarBackgroundColor(primitive.$black, true);
      setStatusBarStyle('light', true);
    }

    setIsModalOpen(false);
  };

  return (
    <>
      <NutritionInformationWrapper focusable>
        {!selectedProduct.hideCalories && (
          <NutritionInformationContainer>
            <TextWithArrowButton
              messageId="nutritionalInformation"
              onPress={() => setIsModalOpen(true)}
            />
          </NutritionInformationContainer>
        )}

        {!selectedProduct.hideNutritionLegalDisclaimer && (
          <InformationTitle accessibilityRole="header" accessibilityLevel={3}>
            {formatMessage({ id: 'information' })}
          </InformationTitle>
        )}

        {enableAllergensDisclaimer && (
          <AllergensDisclaimer allergenGroups={nutritionFacts?.[0]?.allergenGroups} />
        )}

        <LegalDisclaimers
          hideNutritionLegalDisclaimer={selectedProduct.hideNutritionLegalDisclaimer}
        />
      </NutritionInformationWrapper>

      {isModalOpen && <NutritionModal nutritionFacts={nutritionFacts} onDismiss={dismiss} />}
    </>
  );
};

export default NutritionInformation;
